import React , { useContext, useState, useCallback }from 'react';
import { Box, Button, Checkbox, Grid, IconButton, InputAdornment, SvgIcon, TextField, Typography } from '@mui/material';
import { Close, OpenInNew, Search } from '@mui/icons-material';
import { theme } from '../../../theme';
import {  Trash01 } from '../../../assets';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import { TrainingMaterialContext, TrainingMaterialValues } from '../contexts/TrainingMaterialContext';
import { ConfirmationDeleteDialog } from '../../../components/shared/dialogs/ConfirmationDeleteDialog';


const TrainingMaterials=() => {
  const { t,loading, deleteRow, downloadFile, hasWritePermission, handleBulkDelete, selectedItems, handleToggleSelection, filteredData, searchQuery,   
    handleSearchChange, clearAll } = useContext(TrainingMaterialContext) as TrainingMaterialValues;

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingDeleteFileName, setPendingDeleteFileName] = useState<string | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  const handleSingleDeleteClick = useCallback((materialId: string, fileName: string) => {
    setPendingDeleteId(materialId);
    setPendingDeleteFileName(fileName);
    setIsBulkDelete(false);
    setDeleteDialogOpen(true);
  }, []);

  const handleBulkDeleteClick = useCallback(() => {
    setIsBulkDelete(true);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    setDeleteDialogOpen(false);
    if (isBulkDelete) {
      await handleBulkDelete();
    } else if (pendingDeleteId && pendingDeleteFileName) {
      await deleteRow(pendingDeleteId, pendingDeleteFileName);
    }
    setPendingDeleteId(null);
    setPendingDeleteFileName(null);
  }, [isBulkDelete, pendingDeleteId, pendingDeleteFileName, handleBulkDelete, deleteRow]);

  const handleCancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setPendingDeleteId(null);
    setPendingDeleteFileName(null);
  }, []);

  return (
    <>
      <Box
        sx={{
          border: '1px solid #e0e0e0', 
          borderRadius: '8px',
          backgroundColor: '#fff',
          boxShadow: 1,
          padding: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap', 
          gap: 2,
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Search by file name"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{ width: '30%' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton 
                  size="small"
                  onClick={() => handleSearchChange({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>)}
                  edge="end"
                >
                  <Close />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {hasWritePermission && (
          <Button 
            size='lg'
            variant="bad"
            startIcon={<SvgIcon><Trash01 /></SvgIcon>}
            onClick={handleBulkDeleteClick}
            disabled={selectedItems.length === 0} 
          >
            {t('deleteBulkButton')} {selectedItems.length > 0 && `(${selectedItems.length})`}
          </Button> 
        )}
      </Box>
      {filteredData.length === 0 ? (
        <Box
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            backgroundColor: '#fff',
            boxShadow: 1,
            p: 6,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
          }}
        >
          <Typography variant="textMD"
            fontWeight="600"
            sx={{ mb: 2 }}>
            {searchQuery
              ? t('noResultsFound', { defaultValue: 'No results found' })
              : t('noMaterialsTitle', { defaultValue: 'No training materials' })}
          </Typography>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ mb: 3 }}>
            {searchQuery
              ? t('noResultsText', { searchTerm: searchQuery, defaultValue: `No training materials match "${searchQuery}"` })
              : t('noMaterialsText', { defaultValue: 'Upload training materials to get started.' })}
          </Typography>
          {searchQuery && (
            <Button
              variant="secondary"
              size="md"
              onClick={clearAll}>
              {t('clearSearch', { defaultValue: 'Clear Search' })}
            </Button>
          )}
        </Box>
      ) : loading ? (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '400px', 
            width: '100%',
          }}
        >
          <LoadingWheel />
        </Box>
      ) : (
        <Box sx={{ p: 6 ,
          border: '1px solid #e0e0e0', 
          borderRadius: '8px',
          backgroundColor: '#fff',
          boxShadow: 1, 
        }}>
          <Grid container
            spacing={3}>
            <Grid
              item
              xs={12}
              container
              spacing={2}
            >
              <Grid item
                xs={12}
                sm={6}
                container
                spacing={2}
                sx={{ pr: 4, borderBottom: '1px solid #e0e0e0', pb: 2 }}
              >
                {hasWritePermission && (
                  <Grid item
                    xs={1} />
                )}
                <Grid item
                  xs={7}>
                  <Typography>
                    {t('name')}
                  </Typography>
                </Grid>
                <Grid item
                  xs={hasWritePermission ? 2 : 5}
                  display="flex"
                  justifyContent="center">
                  <Typography>
                    {t('view')}
                  </Typography>
                </Grid>
                {hasWritePermission && (
                  <Grid item
                    xs={2}
                    display="flex">
                    <Typography>
                      {t('delete')}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              <Grid item
                xs={12}
                sm={6}
                container
                spacing={2}
                sx={{ display: { xs: 'none', sm: 'flex' }, pl: 6, borderBottom: '1px solid #e0e0e0', pb: 2 }}
              >
                {hasWritePermission && (
                  <Grid item
                    xs={1} />
                )}
                <Grid item
                  xs={7}>
                  <Typography>
                    {t('name')}
                  </Typography>
                </Grid>
                <Grid item
                  xs={hasWritePermission ? 2 : 5}
                  display="flex"
                  justifyContent="center">
                  <Typography>
                    {t('view')}
                  </Typography>
                </Grid>
                {hasWritePermission && (
                  <Grid item
                    xs={2}
                    display="flex">
                    <Typography>
                      {t('delete')}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            { filteredData?.map((item, index) => (
              <Grid
                key={item.Id}
                item
                xs={12}
                sm={6}
                container
                alignItems="center"
                spacing={2}
                sx={{ pr: { sm: index % 2 === 0 ? 4 : 0 }, pl: { sm: index % 2 === 1 ? 6 : 0 } }}
              >
                {hasWritePermission && (
                  <Grid item
                    xs={1}>
                    <Checkbox 
                      checked={selectedItems.includes(item.Id.toString())}
                      onChange={() => handleToggleSelection(item.Id.toString())}
                    />
                  </Grid>
                )}
                <Grid item
                  xs={7}>
                  <Typography 
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}
                    title={item.FileName} 
                  >
                    {item.FileName}
                  </Typography>
                </Grid>

                <Grid item
                  xs={hasWritePermission ? 2 : 5}
                  display="flex"
                  justifyContent="center">
 
                  <IconButton color="primary"
                    onClick={() => downloadFile(item.FileName,item.blobName)}
                  >
                    <OpenInNew />
                  </IconButton>
                </Grid>
                {hasWritePermission && (
                  <Grid item
                    xs={2}
                    display="flex">
                    <IconButton
                      sx={{ color: theme.palette.error[500] }}
                      onClick={() => handleSingleDeleteClick(item.Id.toString(), item.FileName)}
                    >
                      <Trash01 />
                    </IconButton>
                  </Grid>
                )}
                  
              </Grid>
            ))}
          </Grid>
        </Box> )}
      <ConfirmationDeleteDialog
        open={deleteDialogOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        nameOfObject={t('deleteMessage')}
      />
    </>
  );
};

export default TrainingMaterials;